import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

export default function OmMikkel() {
  return (
    <>
      <SEO 
        title="Om Mikkel" 
        description="Læs historien bag Tømrermester Mikkel Juler ApS i Egtved. Vi vægter det gode håndværk, den personlige dialog og det lokale engagement højt." 
      />

      {/* Hero / Header */}
      <section className="bg-brand-wood-light py-12 md:py-16 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex mb-4 text-xs sm:text-sm text-gray-500 gap-2 items-center">
              <Link to="/" className="hover:text-brand-wood">Forside</Link>
              <i className="fa-solid fa-chevron-right text-[10px]" />
              <span className="text-brand-wood-dark font-semibold">Om Mikkel</span>
            </nav>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-brand-dark tracking-tight leading-tight">
              Mød din lokale tømrermester i Egtved
            </h1>
            <p className="mt-4 text-base sm:text-lg text-brand-dark-light leading-relaxed">
              Tømrermester Mikkel Juler ApS er bygget på værdier som ordentlighed, faglig stolthed og tæt dialog med kunden. Læs mere om, hvem vi er, og hvad vi står for.
            </p>
          </div>
        </div>
      </section>

      {/* Historien og værdier */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Billede af Mikkel */}
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <div className="relative">
                <div className="absolute -inset-2 bg-brand-wood/10 rounded-3xl blur-xl" />
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-100">
                  <img 
                    src="https://assets.auxodev.dk/assets/2c036395-f6c9-48ce-982b-c0c810ba745b/6954.jpg" 
                    alt="Tømrermester Mikkel Juler" 
                    className="w-full h-[480px] object-cover"
                  />
                  <div className="absolute top-4 left-4 right-4 bg-brand-green/95 backdrop-blur-xs text-white p-4 rounded-xl flex items-center gap-3">
                    <i className="fa-solid fa-quote-left text-brand-wood text-xl shrink-0" />
                    <p className="text-xs sm:text-sm italic font-medium leading-relaxed">
                      "For mig har det altid været vigtigst med glade og tilfredse kunder."
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                <div className="flex gap-3 items-start p-3 bg-brand-cream/60 rounded-xl border border-gray-100">
                  <div className="w-9 h-9 rounded-lg bg-brand-wood-light text-brand-wood-dark flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-comments text-base" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-brand-dark">Ordentlig dialog</h4>
                    <p className="text-xs text-gray-500 mt-0.5">Vi lytter og er i tæt kontakt.</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start p-3 bg-brand-cream/60 rounded-xl border border-gray-100">
                  <div className="w-9 h-9 rounded-lg bg-brand-wood-light text-brand-wood-dark flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-people-roof text-base" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-brand-dark">Familieværdier</h4>
                    <p className="text-xs text-gray-500 mt-0.5">Glade folk og godt samarbejde.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Historien */}
            <div className="lg:col-span-7 space-y-5">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider block">Historien bag</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-brand-dark tracking-tight">
                Historien om Mikkel
              </h2>
              
              <div className="space-y-4 text-brand-dark-light leading-relaxed text-base sm:text-lg">
                <p>
                  Allerede som barn havde jeg iværksætter-drømmen i mig. Jeg havde min egen lille virksomhed – jeg slog græs. Der blev slået og revet og jeg kiggede altid tilbage på mit arbejde og sagde til mig selv: Mikkel, der er ingen der slår græsset lige så flot som dig!
                </p>
                <p>
                  Jeg blev tømrer fordi jeg altid har set meget op til min storebror. Da han valgte at gå den vej, så var det også det jeg ville!
                </p>
                <p>
                  Allerede i 2009 blev jeg selvstændig. Jeg havde været ansat i et firma, hvor jeg ikke var enig i hvordan man kørte det. Det kunne jeg helt sikkert gøre bedre selv! Og så sprang jeg ud i det...
                </p>
                <p>
                  De første år knoklede jeg helt vildt for at skabe mig en god kundekreds og en stabil virksomhed. For mig har det altid været vigtigst med glade og tilfredse kunder. Det er det jeg vægter allerhøjest som lokal håndværker også selvom det nogle gange kræver at man giver den lidt ekstra.
                </p>
                <p>
                  Jeg er et rigtig stort familiemenneske! Både min far og storebror har været en del af min virksomhed som ansatte og min kone Signe hjælper også til.
                </p>
                <p>
                  Jeg har fået at vide jeg kører en skæv ledelses stil, men hos mig er mine ansatte også min anden familie. Vi er alle lige uanset om du er svend, lærling eller projektansvarlig. Vi er ikke bare kollegaer i hverdagen, men laver også ting sammen efter arbejde. Hos os er der også plads til at vores drenge er med en gang imellem.
                </p>
                <p>
                  Jeg tror på at glade medarbejdere gør et bedre stykke arbejde og det er jeg også sikker på at vores kunder mærker!
                </p>
              </div>

              <div className="pt-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-2xl sm:text-3xl font-bold text-brand-green font-display">
                    Velkommen til min familie
                  </p>
                  <p className="text-sm font-semibold text-brand-wood-dark mt-1">
                    – Mikkel Juler, Tømrermester
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vores værdier i dybden */}
      <section className="bg-brand-cream py-16 md:py-24 border-t border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-brand-wood font-semibold text-sm uppercase tracking-wider block">Hvordan vi arbejder</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-dark">Vores fire kerneværdier</h2>
            <p className="text-brand-dark-light">
              Disse værdier er styrende for alt, hvad vi gør – lige fra det første telefonopkald til den endelige aflevering af dit projekt.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-xs">
              <div className="w-12 h-12 rounded-xl bg-brand-green-light text-brand-green flex items-center justify-center text-xl mb-6">
                <i className="fa-solid fa-compass-drafting" />
              </div>
              <h3 className="text-lg font-bold text-brand-dark mb-3">Godt håndværk</h3>
              <p className="text-brand-dark-light text-sm leading-relaxed">
                Vi går aldrig på kompromis med kvaliteten. Vi anvender kun anerkendte materialer og sikrer, at alle detaljer udføres med stor præcision og faglig stolthed.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-xs">
              <div className="w-12 h-12 rounded-xl bg-brand-green-light text-brand-green flex items-center justify-center text-xl mb-6">
                <i className="fa-solid fa-handshake" />
              </div>
              <h3 className="text-lg font-bold text-brand-dark mb-3">Troværdighed</h3>
              <p className="text-brand-dark-light text-sm leading-relaxed">
                En aftale er en aftale hos os. Vi overholder tidsplaner, priser og aftaler til punkt og prikke, så du undgår ubehagelige overraskelser undervejs.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-xs">
              <div className="w-12 h-12 rounded-xl bg-brand-green-light text-brand-green flex items-center justify-center text-xl mb-6">
                <i className="fa-solid fa-house-chimney" />
              </div>
              <h3 className="text-lg font-bold text-brand-dark mb-3">Lokalt engagement</h3>
              <p className="text-brand-dark-light text-sm leading-relaxed">
                Som lokal tømrermester i Egtved værner vi om vores gode ry i lokalområdet. Vi støtter op om det lokale liv og ønsker at være dit foretrukne valg af tømrer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Private og Erhverv */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider block font-bold">Målgrupper</span>
              <h2 className="text-3xl font-bold text-brand-dark">Vi løser opgaver for både private og erhverv</h2>
              <p className="text-brand-dark-light leading-relaxed">
                Uanset om du er privat boligejer, der drømmer om en ny træterrasse, eller en erhvervsvirksomhed, der skal have renoveret kontorlokalerne, står vi klar til at hjælpe. Vi tilpasser vores arbejdsproces efter jeres behov, så vi forstyrrer mindst muligt i hverdagen eller under den daglige drift.
              </p>
              <div className="space-y-4">
                <div className="flex gap-3 items-center">
                  <i className="fa-solid fa-circle-check text-brand-green" />
                  <span className="font-semibold text-brand-dark">Private: Fokus på boligdrømme, tryghed og flot finish</span>
                </div>
                <div className="flex gap-3 items-center">
                  <i className="fa-solid fa-circle-check text-brand-green" />
                  <span className="font-semibold text-brand-dark">Erhverv: Fokus på effektivitet, overholdte tidsplaner og professionel koordinering</span>
                </div>
              </div>
              <div className="pt-4">
                <Link 
                  to="/kontakt" 
                  className="bg-brand-green hover:bg-brand-green/90 text-white font-semibold px-6 py-3.5 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer text-sm sm:text-base inline-block"
                >
                  Kontakt os for en snak
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://assets.auxodev.dk/assets/2c036395-f6c9-48ce-982b-c0c810ba745b/8116.jpg" 
                alt="Tømrerarbejde udført af Tømrermester Mikkel Juler ApS" 
                className="rounded-2xl shadow-xl object-cover w-full h-[350px]"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
